import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import InterventionForm from '../components/InterventionForm';
import RoleGate from '../components/RoleGate';
import { ROLES } from '../lib/auth/roles';
import { Icons } from '../lib/Icons';
import SessionLogForm from '../components/SessionLogForm';
import InterventionDashboard from '../components/InterventionDashboard';
import InterventionSuggestions from '../components/InterventionSuggestions';
import AppLayout from '../components/AppLayout';
import SchoolPageHeader from '../components/school/SchoolPageHeader';
import CapabilityNotice from '../components/school/CapabilityNotice';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';

const InterventionManagementPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { can } = useSchoolCapabilities();

  // Check if suggestions tab should be active
  useEffect(() => {
    if (searchParams.get('suggestions') === 'true') {
      setActiveTab('suggestions');
    }
    const logId = searchParams.get('log');
    if (logId) {
      setActiveTab('log-session');
      // Preload the intervention for quick logging
      (async () => {
        try {
          const { db } = await import('../firebase');
          const { doc, getDoc } = await import('firebase/firestore');
          const snap = await getDoc(doc(db, 'interventions', logId));
          if (snap.exists()) {
            setSelectedIntervention({ id: snap.id, ...snap.data() });
          }
        } catch (e) {
          console.error('Failed to preload intervention', e);
        }
      })();
    }
  }, [searchParams]);

  const handleInterventionCreated = () => {
    setRefreshTrigger(prev => prev + 1);
    setActiveTab('dashboard');
  };

  const handleSessionLogged = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', Icon: Icons.ChartBar },
    { id: 'suggestions', name: 'AI Suggestions', Icon: Icons.Brain },
    ...(can(SCHOOL_ACTIONS.MANAGE_INTERVENTIONS) ? [{ id: 'create', name: 'Intervention Wizard', Icon: Icons.Plus, role: ROLES.SENCO }] : []),
    { id: 'log-session', name: 'Log Session', Icon: Icons.PencilSquare }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 space-y-4">
          <SchoolPageHeader
            title="Intervention Management"
            description="Create, monitor, and track intervention delivery with a single operational workflow."
            icon={Icons.Target}
            breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Interventions' }]}
            actions={[
              { label: 'Intervention Groups', href: '/intervention-groups', variant: 'secondary' },
            ]}
          />
          {!can(SCHOOL_ACTIONS.MANAGE_INTERVENTIONS) && (
            <CapabilityNotice
              title="Intervention editing is restricted"
              body="Your role can view interventions and session logs, but creation and major edits are limited."
            />
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  >
                  <tab.Icon className="w-4 h-4" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'dashboard' && (
            <InterventionDashboard key={refreshTrigger} />
          )}

          {activeTab === 'suggestions' && (
            <InterventionSuggestions />
          )}

          {activeTab === 'create' && (
            <RoleGate allow={[ROLES.SEN_CO, ROLES.SENCO]} fallback={<div className="p-4 text-gray-600">Only SENCOs can create interventions.</div>}>
              <InterventionForm onInterventionCreated={handleInterventionCreated} />
            </RoleGate>
          )}

          {activeTab === 'log-session' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Intervention to Log Session</h2>
                <InterventionSelector 
                  onInterventionSelected={setSelectedIntervention}
                  refreshTrigger={refreshTrigger}
                />
              </div>
              
              {selectedIntervention ? (
                <SessionLogForm 
                  interventionId={selectedIntervention.id}
                  onSessionLogged={handleSessionLogged}
                />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2">Please select an intervention above to log a session</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

// Helper component for selecting interventions when logging sessions
  const InterventionSelector = ({ onInterventionSelected, refreshTrigger }) => {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchInterventions();
  }, [refreshTrigger]);

  const fetchInterventions = async () => {
    try {
      setLoading(true);
      const { collection, getDocs, query, where, orderBy } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const { getCurrentSchoolId } = await import('../lib/auth/schoolContext');
      const schoolId = await getCurrentSchoolId();
      
      const interventionsQuery = query(
        collection(db, 'interventions'),
        where('schoolId', '==', schoolId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      const interventionsSnapshot = await getDocs(interventionsQuery);
      const interventionsData = interventionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInterventions(interventionsData);
    } catch (error) {
      console.error('Error fetching interventions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (interventions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No active interventions found. Create an intervention first.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {interventions.map((intervention) => (
        <button
          key={intervention.id}
          onClick={() => onInterventionSelected(intervention)}
          className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all text-left bg-white"
        >
          <h3 className="font-medium text-gray-900 mb-2">{intervention.name}</h3>
          <p className="text-sm text-gray-600 mb-2">{intervention.type}</p>
          <p className="text-xs text-gray-500">
            {intervention.students?.length || 0} student(s) • {intervention.frequency}
          </p>
        </button>
      ))}
    </div>
  );
};

export default InterventionManagementPage; 
